import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, ArrowRight, Layers, MessageSquareText, Search, Sparkles, Tags, X } from 'lucide-react';
import { AudienceLevel, ConversationRead, LEVEL_LABELS, LEVEL_ORDER, PathwayTemplate } from '../../types/pathway';
import { PathwayButton } from './PathwayButton';
import { TopicEditor } from './TopicEditor';
type EntryMode = 'notes' | 'topics';
interface StartPanelProps {
  mode: EntryMode;
  notes: string;
  topics: string;
  audience: AudienceLevel | 'any';
  error: string | null;
  liveRead: ConversationRead;
  templates: PathwayTemplate[];
  activeTerms: string[];
  suggestedTerms: string[];
  vocabulary: string[];
  detectedLevel: AudienceLevel | null;
  onModeChange: (mode: EntryMode) => void;
  onNotesChange: (value: string) => void;
  onTopicsChange: (value: string) => void;
  onAudienceChange: (value: AudienceLevel | 'any') => void;
  onToggleTerm: (term: string) => void;
  onClearNotes: () => void;
  onSearch: () => void;
  onBrowseAll: () => void;
  onUseTemplate: (template: PathwayTemplate) => void;
}
const EXAMPLE = 'Spoke with the VP of People at Meridian Health. They promoted 24 senior nurses into manager roles after a reorg and turnover has spiked. The new managers are still doing the clinical work themselves and avoiding performance conversations.';
export function StartPanel({
  mode,
  notes,
  topics,
  audience,
  error,
  liveRead,
  templates,
  activeTerms,
  suggestedTerms,
  vocabulary,
  detectedLevel,
  onModeChange,
  onNotesChange,
  onTopicsChange,
  onAudienceChange,
  onToggleTerm,
  onClearNotes,
  onSearch,
  onBrowseAll,
  onUseTemplate
}: StartPanelProps) {
  const tabs: {
    id: EntryMode;
    label: string;
    icon: typeof Search;
  }[] = [{
    id: 'notes',
    label: 'From conversation notes',
    icon: MessageSquareText
  }, {
    id: 'topics',
    label: 'By topic or skill',
    icon: Tags
  }];
  const typingRef = useRef<number | null>(null);
  const detectedTerms = liveRead.terms;
  // Headcount has no control of its own, so it needs its own dismiss.
  const [dismissedHeadcount, setDismissedHeadcount] = useState<number | null>(null);
  const showHeadcount = liveRead.headcount !== null && liveRead.headcount !== dismissedHeadcount;
  const stopTyping = () => {
    if (typingRef.current !== null) {
      window.clearInterval(typingRef.current);
      typingRef.current = null;
    }
  };

  // Nothing to recommend against until there is at least one active topic
  // (notes) or a typed term (topics tab).
  const canRecommend = mode === 'notes' ? activeTerms.length > 0 : topics.trim().length > 0;
  const clearNotes = () => {
    stopTyping();
    setDismissedHeadcount(null);
    onClearNotes();
  };
  useEffect(() => () => {
    if (typingRef.current !== null) window.clearInterval(typingRef.current);
  }, []);
  const typeExample = () => {
    stopTyping();
    setDismissedHeadcount(null);
    let index = 0;
    onNotesChange('');
    typingRef.current = window.setInterval(() => {
      index = Math.min(index + 4, EXAMPLE.length);
      onNotesChange(EXAMPLE.slice(0, index));
      if (index >= EXAMPLE.length && typingRef.current !== null) {
        window.clearInterval(typingRef.current);
        typingRef.current = null;
      }
    }, 16);
  };
  return <div className="space-y-8" data-id="element-1895" data-mp="w1pmpzqzo5xh">
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1896" data-mp="8dy9mtxmjov5">
        <div className="flex border-b border-gray-200 px-5" data-id="element-1897" data-mp="8bv93fyuidc6">
          {tabs.map(tab => {
          const active = mode === tab.id;
          return <button key={tab.id} type="button" onClick={() => onModeChange(tab.id)} aria-pressed={active} className={active ? 'flex items-center gap-2 border-b-2 border-[#00638C] pb-4 pt-4 pr-6 text-sm font-medium text-[#00638C] transition-colors' : 'flex items-center gap-2 border-b-2 border-transparent pb-4 pt-4 pr-6 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900'} data-id="element-1898" data-mp="i4sx21mlwui6">
                <tab.icon size={16} data-id="element-1899" data-mp="2t2sue8i4toie" />
                {tab.label}
              </button>;
        })}
        </div>

        <div className="p-6" data-id="element-1900" data-mp="2xuugsn90kvm5">
          {mode === 'notes' ? <>
              <label htmlFor="conversation-notes" className="block text-sm font-semibold text-gray-900" data-id="element-1901" data-mp="32tx8z73wb7cp">
                What did the prospect tell you?
              </label>
              <p className="mt-1 text-sm text-gray-600" data-id="element-1902" data-mp="3csmq2xnywnrw">
                Paste your call notes in plain language. We’ll pull out the
                themes and audience and recommend modules against them.
              </p>
              <textarea id="conversation-notes" value={notes} onChange={e => onNotesChange(e.target.value)} rows={6} placeholder={EXAMPLE} className="mt-3 w-full resize-y rounded-lg border border-gray-200 p-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1903" data-mp="1ifdhd838x6ap" />
              <div className="mt-2 flex items-center gap-4" data-id="element-1904" data-mp="114rfqm514ff3">
                <button type="button" onClick={typeExample} className="text-xs font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1905" data-mp="11257sle87xwc">
                  Use an example
                </button>
                {notes.trim().length > 0 && <button type="button" onClick={clearNotes} className="text-xs font-medium text-gray-500 transition-colors hover:text-[#DB3725]" data-id="element-1906" data-mp="1juzbukaba5ls">
                    Clear notes
                  </button>}
              </div>

              <AnimatePresence initial={false} data-id="element-1907" data-mp="9xht0ganxq4d">
                {(activeTerms.length > 0 || suggestedTerms.length > 0 || notes.trim().length > 0) && <motion.div layout initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 6
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} aria-live="polite" className="mt-4 rounded-lg bg-[#30718D]/5 p-4" data-id="element-1908" data-mp="wjjxjldasex">
                    <TopicEditor instanceId="notes" heading="Suggested topics from your notes" activeTerms={activeTerms} detectedTerms={detectedTerms} suggestedTerms={suggestedTerms} suggestedLabel="Also consider" vocabulary={vocabulary} onToggleTerm={onToggleTerm} footer={showHeadcount ? <div className="mt-4 flex items-start gap-2 border-t border-[#30718D]/15 pt-3" data-id="element-1910" data-mp="2ks9r4fu14069">
                            <p className="flex-1 text-xs text-gray-600" data-id="element-1911" data-mp="2yrswj0vggxev">
                              Sounds like about {liveRead.headcount} people —
                              pick a cohort later to record the exact size.
                            </p>
                            <button type="button" onClick={() => setDismissedHeadcount(liveRead.headcount)} aria-label="Dismiss the detected headcount" className="flex-shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:text-gray-700" data-id="element-1912" data-mp="33qchoz286566">
                              <X size={12} data-id="element-1913" data-mp="3me7qe4izcssf" />
                            </button>
                          </div> : null} data-id="element-1909" data-mp="3de84hap0kvdg" />
                  </motion.div>}
              </AnimatePresence>
            </> : <>
              <label htmlFor="topic-input" className="block text-sm font-semibold text-gray-900" data-id="element-1914" data-mp="1bmwoonlyivyl">
                Topics or skills
              </label>
              <p className="mt-1 text-sm text-gray-600" data-id="element-1915" data-mp="iy7jodhs66hk">
                Separate terms with commas. Every term is searched and the
                results are merged.
              </p>
              <input id="topic-input" value={topics} onChange={e => onTopicsChange(e.target.value)} placeholder="leadership, change management, retention" className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1916" data-mp="nvhe67k118c9" />
            </>}

          <div className="mt-5 border-t border-gray-100 pt-5" data-id="element-1917" data-mp="3jvb6e95s9vkf">
            <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-1918" data-mp="3eunhpu511qtd">
              <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1919" data-mp="3aud17njlzi09">
                Who is this for?
              </span>
              {detectedLevel && audience === detectedLevel && <span className="inline-flex items-center gap-1 text-xs text-[#30718D]" data-id="element-1920" data-mp="312z6dt2lr6dg">
                  <Sparkles size={11} aria-hidden="true" data-id="element-1921" data-mp="2w45ta1r89hdt" />
                  Set from your notes — change it if that’s not right
                </span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-2" data-id="element-1922" data-mp="2n3zi4mj04tht">
              {(['any', ...LEVEL_ORDER] as (AudienceLevel | 'any')[]).map(level => {
              const active = audience === level;
              const fromNotes = detectedLevel === level;
              return <button key={level} type="button" onClick={() => onAudienceChange(level)} aria-pressed={active} className={active ? 'inline-flex items-center gap-1.5 rounded-full border border-[#30718D] bg-[#30718D]/10 px-3 py-1.5 text-sm font-medium text-[#30718D] transition-colors' : 'inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50'} data-id="element-1923" data-mp="z9avq2kh16fu">
                      {fromNotes && <Sparkles size={11} aria-hidden="true" data-id="element-1924" data-mp="26m2t2j9rp937" />}
                      {level === 'any' ? 'Not sure yet' : LEVEL_LABELS[level]}
                    </button>;
            })}
            </div>
          </div>

          {error && <p role="alert" className="mt-4 flex items-center gap-2 text-sm text-[#DB3725]" data-id="element-1925" data-mp="2993ubj2hueul">
              <AlertCircle size={15} data-id="element-1926" data-mp="206kvda1twmdm" />
              {error}
            </p>}

          <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-1927" data-mp="247ee7p0ezazn">
            <PathwayButton onClick={onSearch} disabled={!canRecommend} data-id="element-1928" data-mp="249qxq6ltltet">
              <Search size={18} data-id="element-1929" data-mp="2rxsaxsty1y0k" />
              Recommend Modules
            </PathwayButton>
            <PathwayButton variant="secondary" onClick={onBrowseAll} data-id="element-1930" data-mp="2wv0k53x46rpo">
              Browse All Modules
            </PathwayButton>
          </div>
        </div>
      </div>

      <section aria-labelledby="templates-heading" data-id="element-1931" data-mp="2n60fp4zuf443">
        <div className="mb-4 flex items-end justify-between gap-4" data-id="element-1932" data-mp="2n4fn3kx39v8v">
          <div data-id="element-1933" data-mp="3avbam5uii4ab">
            <h2 id="templates-heading" className="text-xl font-bold text-gray-900" data-id="element-1934" data-mp="3aujn7ib2jcv4">
              Start from a proven pathway
            </h2>
            <p className="mt-1 text-sm text-gray-600" data-id="element-1935" data-mp="3pijs1oeez5uq">
              Pre-built sequences your team already sells. Add one and edit from
              there.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2" data-id="element-1936" data-mp="3ghveeusxn7id">
          {templates.map((template, index) => <motion.button key={template.id} type="button" onClick={() => onUseTemplate(template)} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          delay: index * 0.04,
          ease: [0.23, 1, 0.32, 1]
        }} className="group flex h-full flex-col rounded-xl border border-gray-100 bg-white p-5 text-left shadow-sm transition-shadow hover:shadow-md" data-id="element-1937" data-mp="3bjak1c0d3i1u" data-mpi={index}>
              <div className="flex items-center gap-3" data-id="element-1938" data-mp="2nsod5o23g3tf">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#30718D]/10" data-id="element-1939" data-mp="2iu3evu6eju7m">
                  <Layers size={16} className="text-[#30718D]" data-id="element-1940" data-mp="28pn0l58kml5x" />
                </span>
                <h3 className="text-base font-semibold text-gray-900" data-id="element-1941" data-mp="2dohsqxsfhm2p">
                  {template.name}
                </h3>
              </div>
              <p className="mt-3 flex-grow text-sm text-gray-600" data-id="element-1942" data-mp="2imjddn5i5tga">
                {template.summary}
              </p>
              <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3" data-id="element-1943" data-mp="1pzygy8a66r5v">
                <span className="text-xs text-gray-500" data-id="element-1944" data-mp="1uyrxyaoo10as">
                  {template.moduleIds.length} modules ·{' '}
                  {LEVEL_LABELS[template.audience]}
                </span>
                <span className="flex items-center gap-1 text-sm font-medium text-[#DB3725]" data-id="element-1945" data-mp="24oups7k1w87j">
                  Use
                  <ArrowRight size={14} className="transition-transform duration-150 group-hover:translate-x-0.5" data-id="element-1946" data-mp="179z0g9seake2" />
                </span>
              </div>
            </motion.button>)}
        </div>
      </section>
    </div>;
}